import React from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { usageTypeLabel } from 'admin/constants/usageType';
import { Button } from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import { ShopApplicationFormValues, TagSummaryEntry } from './index';

type Props = {
  isOpen: boolean;
  formValues: ShopApplicationFormValues | null;
  originalValues: ShopApplicationFormValues | null;
  privateMoneyNames: string[];
  originalPrivateMoneyNames: string[];
  tagSummary: TagSummaryEntry[];
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
};

const modalStyles = {
  overlay: {
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    padding: 40,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    maxHeight: '95vh',
    overflow: 'auto',
  },
};

const ModalHeader = styled(Headings.h4)`
  font-size: 20px;
  text-align: center;
  margin: 0;
  width: 100%;
  font-weight: 700;
`;

const ModalContent = styled.div`
  margin: 40px 0;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 24px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubSection = styled.div`
  display: flex;
  gap: 24px;
`;

const SectionLabel = styled.label`
  width: 180px;
  color: #9f9f9f;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  flex-shrink: 0;
  display: flex;
  column-gap: 12px;
  row-gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  align-content: flex-start;
`;

const InfoText = styled.p<{ isBold?: boolean }>`
  margin: 0;
  color: #222;
  font-size: 16px;
  font-style: normal;
  font-weight: ${({ isBold = false }) => (isBold ? '700' : '400')};
  line-height: 24px;
  flex: 1;
  word-break: break-word;
`;

const EditedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #21bf25;
  width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
`;

const EditedBadge = () => <EditedBadgeContainer>変更あり</EditedBadgeContainer>;

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const ConfirmModal = ({
  isOpen,
  formValues,
  originalValues,
  privateMoneyNames,
  originalPrivateMoneyNames,
  tagSummary,
  onClose,
  onConfirm,
  isSubmitting,
}: Props) => {
  if (!formValues) return null;

  // Compare values to detect changes (only for change mode where originalValues exists)
  const isChanged = (key: keyof ShopApplicationFormValues) => {
    if (!originalValues) return false;
    if (key === 'private_money_ids') {
      return !arraysEqual(formValues.private_money_ids, originalValues.private_money_ids);
    }
    return formValues[key] !== originalValues[key];
  };

  const isPrivateMoneyChanged = isChanged('private_money_ids');

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={!isSubmitting}
      closeTimeoutMS={200}
      ariaHideApp={false}
      style={modalStyles}
    >
      <ModalHeader>申請内容の確認</ModalHeader>

      <ModalContent>
        <Section>
          <SubSection>
            <SectionLabel>
              加盟するマネー {isPrivateMoneyChanged && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isPrivateMoneyChanged}>
              {privateMoneyNames.join(', ') || '-'}
            </InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              店舗名 {isChanged('shop_name') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('shop_name')}>{formValues.shop_name || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              店舗名（フリガナ） {isChanged('shop_name_furigana') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('shop_name_furigana')}>
              {formValues.shop_name_furigana || '-'}
            </InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              郵便番号 {isChanged('postal_code') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('postal_code')}>{formValues.postal_code || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              住所 {isChanged('address') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('address')}>{formValues.address || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              電話番号 {isChanged('tel') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('tel')}>{formValues.tel || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              メールアドレス {isChanged('email') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('email')}>{formValues.email || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              担当者名 {isChanged('contact_person_name') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('contact_person_name')}>
              {formValues.contact_person_name || '-'}
            </InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              担当者名（フリガナ） {isChanged('contact_person_name_furigana') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('contact_person_name_furigana')}>
              {formValues.contact_person_name_furigana || '-'}
            </InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              店舗形態 {isChanged('usage_type') && <EditedBadge />}
            </SectionLabel>
            <InfoText isBold={isChanged('usage_type')}>
              {usageTypeLabel(formValues.usage_type) || '-'}
            </InfoText>
          </SubSection>

          {tagSummary.map((entry) => (
            <SubSection key={entry.groupId}>
              <SectionLabel>
                {entry.groupName} {entry.changed && <EditedBadge />}
              </SectionLabel>
              <InfoText isBold={entry.changed}>{entry.itemNames.join('、') || '-'}</InfoText>
            </SubSection>
          ))}
        </Section>
      </ModalContent>

      <ModalFooter>
        <Button variant="neutral" onClick={onClose} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? '送信中...' : '審査の申請'}
        </Button>
      </ModalFooter>
    </ReactModal>
  );
};

export default ConfirmModal;
